import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import {
  AdditionalProductsSummaryPoint,
  Address,
  IAktionen,
  IBusinessData,
  IdCard,
  IERechnungsInfo,
  IProduktSelektion,
  Product,
  SummaryContractInfo,
  TariffSelectionCardData,
} from '@mwe/models';
import { getAddressGroupFormatted, sortProductsByCategory } from '@mwe/utils';

@Component({
  selector: 'mwe-products-summary',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsSummaryComponent {
  collapseId = input<string>();
  address = input<Address>();
  invoiceAddress = input<Address>();
  initialState = input<'expanded' | 'collapsed'>();
  stromOrGasProducts = input<Product[] | undefined, Product[] | undefined>(undefined, {
    transform: value => sortProductsByCategory(value, true) as Product[],
  });
  fernwaermeProducts = input<Product[]>();
  registrationAt = input<string>();
  registerStromGasTitle = input<string>('summary.productsFromWEV');
  registerStromGasText = input<string>();
  registerStromGasInfoMessage = input<string>();
  registerFernwaermeTitle = input<string>('summary.checkRegistrationForProducts');
  registerFernwaermeSubTitle = input<string>('summary.checkRegistrationForProducts');
  registerFernwaermeText = input<string>();
  registerFernwaermeTextPlaceholder = input(new Map<string, string>());
  birthDate = input<string>();
  idCard = input<IdCard>();
  salutation = input<string>();
  title = input<string>();
  titlePrefix = input<string>();
  titleSuffix = input<string>();
  firstname = input<string>();
  lastname = input<string>();
  email = input<string>();
  eRechnungInfo = input<IERechnungsInfo>();
  paymentIcon = input<string>();
  paymentText = input<string>();
  voucherInfo = input<IAktionen>();
  showMeterReading = input(true);
  invoiceInfoAlertMessage = input('');
  isCollapsable = input(true);
  personalBillingTitle = input('summary.personalBilling');
  areBankAccountInfoTextsVisible = input(false);
  contactDataEmailAddress = input<string>();
  showStromGasTarifDetails = input(true);
  additionalBulletPointsStromGas = input<AdditionalProductsSummaryPoint[]>();
  additionalBulletPointsFernwaerme = input<AdditionalProductsSummaryPoint[]>();
  showFernwaermeInvoiceData = input(false);
  isPaymentInfoVisible = input(true);
  showTariffDetails = input(true);
  showTariffDetailsLink = input(false);
  productSelection = input<IProduktSelektion>();
  customerDataType = input<string>();
  businessData = input<IBusinessData>();
  grundpreisLabelOverwrite = input<string>();
  mgmCode = input<string>();
  isCollapseHeaderVisible = input(true);
  isKundeWerden = input(false);
  showWaermeTariffClassesAsideOfIcon = input(false);
  useNewWaermeCards = input(false);
  infoTextCardLabel = input<string>();
  showDeliveryInfosContractInfo = input(false);
  countryCode = input<string>();
  telefonNumber = input<string>();
  selectedTariffs = input<TariffSelectionCardData[]>([]);
  contractInfos = model<SummaryContractInfo[]>(null);

  readonly voucherStrom = computed(() => {
    if (!this.voucherInfo()?.infoTextKey?.length) return '';

    if (this.voucherInfo().infoTextKey[0].toLowerCase().startsWith('strom')) {
      return this.voucherInfo().infoText[0];
    }

    return this.voucherInfo().infoText[1];
  });

  readonly voucherGas = computed(() => {
    if (!this.voucherInfo()?.infoTextKey?.length) return '';

    if (this.voucherInfo().infoTextKey[0].toLowerCase().startsWith('strom')) {
      return this.voucherInfo().infoText[1];
    }

    return this.voucherInfo().infoText[0];
  });

  readonly productCategories = computed(() => {
    let allProducts = [];

    if (Array.isArray(this.stromOrGasProducts())) {
      allProducts = allProducts.concat(this.stromOrGasProducts());
    }

    if (Array.isArray(this.fernwaermeProducts())) {
      allProducts = allProducts.concat(this.fernwaermeProducts());
    }

    return allProducts.map(product => product.category);
  });

  readonly formattedAddress = computed(() => {
    return this.invoiceAddress() ? getAddressGroupFormatted(this.invoiceAddress(), true) : getAddressGroupFormatted(this.address(), true);
  });

  readonly fullNameWithTitle = computed<string>(() => {
    let value = '';

    if (this.salutation()) {
      value += `${this.salutation()} `;
    }
    if (this.titlePrefix()) {
      value += `${this.titlePrefix()} `;
    }
    if (this.title()) {
      value += `${this.title()} `;
    }
    value += `${this.firstname()} ${this.lastname()}`;
    if (this.titleSuffix()) {
      value += ` ${this.titleSuffix()}`;
    }

    return value;
  });

  readonly telefonNumberWithCountryCode = computed<string>(() => {
    const countryCode = this.countryCode();
    const telefonNumber = this.telefonNumber();

    return countryCode && telefonNumber ? `+${countryCode} ${telefonNumber}` : '';
  });
}
